import type { ContactFormState, FormErrors, SupportInfo } from "@/lib/types";
import { isFieldValid } from "@/lib/utils";
import { sendTicketToSupport, type ServerResponse } from "@/services";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

export const useContactSupport = () => {
  const [form, setForm] = useState<ContactFormState>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const updateField = (field: keyof ContactFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field] || !isFieldValid(field, value)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };
  const contactSupportMutation = useMutation({
    mutationFn: async (supportInfo: SupportInfo) => {
      const response = await sendTicketToSupport(supportInfo);
      return response;
    },
    onSuccess: () => {
      toast.success(
        "We've received your message and will get back to you shortly.",
        {
          classNames: {
            toast:
              "!bg-secondary !text-start !text-secondary-foreground !border-[color-mix(in_oklch,var(--secondary),var(--foreground)_25%)]",
          },
        },
      );

      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    },
    onError: (error: ServerResponse) => {
      const errorMessage = error.ApiMessages.map((msg) => msg.split(" - ")[1])
        .filter(Boolean)
        .join(", ");
      toast.error(errorMessage || "There was an error submitting form", {
        classNames: {
          toast: "!bg-red-50 !text-start !text-red-700",
        },
      });
    },
  });

  return {
    form,
    errors,
    setErrors,
    updateField,
    contactSupportMutation,
  };
};
